import { useStoreActions, useStoreState } from "../../store";
import React from "react";
import "./CSMManagerModeToggle.css";

export type CSMManagerMode = "active" | "completed";



type CSMManagerModeToggleProps = {
  /** Count badge for completed jobs (displayed next to "Completed jobs") */
  completedCount?: number;
  /** Optional aria-label for the container */
  ariaLabel?: string;
  /** Optional callback when user switches mode (informative only; state is internal) */
  onChange?: (mode: CSMManagerMode) => void;
  /** Optional className passthrough for outermost wrapper */
  className?: string;
};

const TABS: Array<{ id: CSMManagerMode; label: string }> = [
  { id: "active", label: "Active jobs" },
  { id: "completed", label: "Completed jobs" },
];


const CSMManagerModeToggle: React.FC<CSMManagerModeToggleProps> = ({
  completedCount = 0,
  ariaLabel = "Jobs Mode Toggle",
  onChange,
  className = "",
}) => {
  // Derive the selected tab from the (persisted) store rather than keeping a
  // separate local copy. The store is persisted to sessionStorage, so on a
  // refresh isActiveJobMode is restored correctly; a local useState here would
  // reset to "active" on mount and desync the highlighted tab from the page
  // actually rendered (Active tab shown while the Completed page loads).
  const isActiveJobMode = useStoreState((s) => s.appSettings.isActiveJobMode);
  const setIsActiveJobMode = useStoreActions((a) => a.appSettings.setIsActiveJobMode);

  const mode: CSMManagerMode = isActiveJobMode ? "active" : "completed";
  const isActive = mode === "active";
  const isCompleted = mode === "completed";

  const setSelected = (next: CSMManagerMode) => {
    onChange?.(next);
    // active tab  -> isActiveJobMode = true
    // completed   -> isActiveJobMode = false
    setIsActiveJobMode(next === "active");
  };

  // Keyboard navigation: Left/Right/Home/End for tabs
  const onKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    const currentIndex = TABS.findIndex((t) => t.id === mode);
    if (currentIndex < 0) return;

    if (e.key === "ArrowRight") {
      e.preventDefault();
      const next = TABS[(currentIndex + 1) % TABS.length].id;
      setSelected(next);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      const next = TABS[(currentIndex - 1 + TABS.length) % TABS.length].id;
      setSelected(next);
    } else if (e.key === "Home") {
      e.preventDefault();
      setSelected(TABS[0].id);
    } else if (e.key === "End") {
      e.preventDefault();
      setSelected(TABS[TABS.length - 1].id);
    }
  };

  return (
    <div
      data-tab="Tab 1"
      className={`csm-toggle ${className}`}
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
    >
      <div className="csm-toggle__row">
        {/* Active jobs */}
        <button
          type="button"
          role="tab"
          aria-selected={isActive}
          aria-controls="csm-panel-active"
          id="csm-tab-active"
          data-csm-step="home.active-jobs-tab"
          tabIndex={isActive ? 0 : -1}
          className={`csm-toggle__tab ${isActive ? "is-selected" : ""}`}
          onClick={() => setSelected("active")}
        >
          <span className="csm-toggle__label csm-toggle__label--active">
            Active jobs
          </span>
        </button>

        {/* Completed jobs + centered badge */}
        <button
          type="button"
          role="tab"
          aria-selected={isCompleted}
          aria-controls="csm-panel-completed"
          id="csm-tab-completed"
          data-csm-step="home.completed-jobs-tab"
          tabIndex={isCompleted ? 0 : -1}
          className={`csm-toggle__tab ${isCompleted ? "is-selected" : ""}`}
          onClick={() => setSelected("completed")}
        >
          <span className="csm-toggle__label csm-toggle__label--completed">
            Completed jobs
          </span>
          <span
            className="csm-toggle__badge"
            aria-label={`${completedCount} completed jobs`}
          >
            {completedCount}
          </span>
        </button>
      </div>

      {/* Optional tab panels (visually hidden by default unless you want to render content) */}
      <div id="csm-panel-active" role="tabpanel" aria-labelledby="csm-tab-active" hidden={!isActive} />
      <div id="csm-panel-completed" role="tabpanel" aria-labelledby="csm-tab-completed" hidden={!isCompleted} />
    </div>
  );
};

export default CSMManagerModeToggle;
